import React from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  useToast,
  Flex
} from '@homebotapp/hb-react-component-catalog'

import { defineMessages, useIntl } from 'react-intl'
import { useCreateMarketInterest } from '../../../hooks/gallery/useCreateMarketInterest'
import { useGetClientId } from '../../../hooks/useGetClientId'

const MSG = defineMessages({
  title: {
    id: 'NoFeedTypeModal.title',
    defaultMessage: 'Missing Market'
  },
  description: {
    id: 'NoFeedTypeModal.description',
    defaultMessage: "You didn't have this market added in for you."
  },
  addThisMarket: {
    id: 'NoFeedTypeModal.addThisMarket',
    defaultMessage: 'Add this market'
  },
  zipCodeCantBeAdded: {
    id: 'NoFeedTypeModal.zipCodeCantBeAdded',
    defaultMessage: 'The zip code could not be added'
  },
  cancel: {
    id: 'NoFeedTypeModal.cancel',
    defaultMessage: 'Cancel'
  }
})

interface NoFeedTypeModalProps {
  isOpen: boolean
  onClose: () => void
  onMarketAdded: () => void
  market?: string
}

export const NoFeedTypeModal: React.FC<NoFeedTypeModalProps> = ({ isOpen, onClose, onMarketAdded, market }) => {
  const intl = useIntl()
  const toast = useToast()
  const clientId = useGetClientId()

  const { mutateAsync: createMarketInterest, isLoading: creatingMarketInterest } = useCreateMarketInterest()

  const onAddMarket = async (): Promise<void> => {
    const createdMarketInterest = await createMarketInterest({
      input: {
        clientId,
        zipCode: market
      }
    })
    if (createdMarketInterest.createMarketInterest.id) {
      onMarketAdded()
    } else {
      toast({
        description: intl.formatMessage(MSG.zipCodeCantBeAdded),
        status: 'warning'
      })
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size='md'>
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader textAlign='center' pt={8} pb={4}>
          <Text size='2xl' fontWeight='bold' mb={0}>
            {intl.formatMessage(MSG.title)}
          </Text>
        </ModalHeader>
        <ModalBody textAlign='center' px={8}>
          <Text color='neutral.700' mb={0}>
            {intl.formatMessage(MSG.description, { market: market || 'your market' })}
          </Text>
        </ModalBody>
        <ModalFooter as={Flex} alignItems='center' justifyContent='space-between' gap={3} pb={8} px={8}>
          <Button as={Flex} flex='1' colorScheme='secondary' onClick={onClose}>
            {intl.formatMessage(MSG.cancel)}
          </Button>
          <Button as={Flex} flex='1' isLoading={creatingMarketInterest} colorScheme='primary' onClick={onAddMarket}>
            {intl.formatMessage(MSG.addThisMarket)}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
